/**
 * ISF 申报凭证
 *
 * 设计来源：设计文档 §7.2.1（ISF）/ §7.3（区块：单证信息 / 贸易参与方 / CARGOS）
 * - CARGO TYPE 渲染英文标准描述（映射表为 builder 常量），未命中原样输出代码
 * - FILER CODE = hblScac（D7），空回退 filerCode
 * - CARGOS 的 HS 与 COUNTRY OF ORIGIN 必须来自同一货物行
 */
import type { CertificateBuilder, CertificateContext, CertificateData, CertificateSection } from '../types'
import { registerCertificateBuilder, CERTIFICATE_TITLES } from './registry'
import {
  DASH,
  FieldCollector,
  billWithScac,
  codeWithType,
  dedupeMissing,
  dictCountry,
  filingStatus,
  findParty,
  formatDateTime,
  gridSection,
  latestReceipt,
  logsSection,
  partyAddress,
  pick,
  receiptValue,
  rowsOf,
  tableSection,
  text,
} from './shared'

/** CBP 货运申报类型（源自 isf.declarationOptions.filingType） */
const FILING_TYPES: Record<string, string> = {
  '01': '01 - Standard/Regular Filings',
  '02': '02 - To Order Shipments',
  '03': '03 - Household Goods/Personal Effects',
  '04': '04 - Military/Government',
  '05': '05 - Diplomatic Shipment',
  '06': '06 - Carnet',
  '07': '07 - US Return Goods',
  '08': '08 - FTZ Shipments',
  '09': '09 - International Mail',
  '10': '10 - Outer Continental Shelf',
  '11': '11 - Informal',
}

/** CBP 担保业务代码（源自 isf.declarationOptions.bondActivityCode） */
const BOND_ACTIVITY_CODES: Record<string, string> = {
  '01': '01 - Importer or Broker',
  '02': '02 - Custodian of Bonded Merchandise',
  '03': '03 - International Carrier',
  '04': '04 - Foreign Trade Zone Operator',
  '16': '16 - ISF Bond',
}

/** CBP 担保类型（源自 isf.enum.bondType） */
const BOND_TYPES: Record<string, string> = {
  '8': 'Continuous Bond',
  '9': 'Single Entry Bond',
}

function mapped(map: Record<string, string>, value: unknown): string {
  const raw = text(value)
  if (!raw) return ''
  return map[raw] ?? raw
}

export const buildIsfCertificate: CertificateBuilder = (
  context: CertificateContext
): CertificateData => {
  const detail = context.detail ?? {}
  const dict = context.dict
  const receipt = latestReceipt(context.receipts)
  const missing: string[] = []

  const info = new FieldCollector(missing)
  info
    .add('DOCUMENTTYPE', pick(detail, 'documentType'))
    .add(
      'FILING STATUS',
      filingStatus(
        pick(receipt, 'receiptStatus') || pick(detail, 'customsStatus'),
        context.statusLabel,
        pick(receipt, 'sendType')
      )
    )
    .add('HBL#', billWithScac(pick(detail, 'hblScac'), pick(detail, 'hblNo')))
    .add('TRANSMITTED(ET)', formatDateTime(pick(detail, 'sendTime') || pick(receipt, 'sendTime')))
    .add('TRANSACTION NO', pick(detail, 'mrnNo'))
    .add('FILER CODE', pick(detail, 'hblScac') || pick(detail, 'filerCode'))
    .add('COMPLETENESS', pick(detail, 'customsStatus'))
    .add(
      'ISF IMPORTER REF #',
      codeWithType(detail.isfImporterType, detail.isfImporterNumber)
    )
    .add('CARGO TYPE', mapped(FILING_TYPES, detail.declarationType))
    .add(
      'IMPORTER OF REC #',
      codeWithType(detail.importerOfRecordType, detail.importerOfRecordNumber)
    )
    .add('BOND TYPE', mapped(BOND_TYPES, detail.bondType))
    .add(
      'CONSIGNEE REF NO',
      codeWithType(detail.consigneeRefType, detail.consigneeRefNumber)
    )
    .add('BOND ACTIVITY CODE', mapped(BOND_ACTIVITY_CODES, detail.bondActivityCode))
    .add(
      'BOND REF #',
      codeWithType(detail.bondRefType, detail.bondRefNumber) || pick(detail, 'bondNo')
    )

  const parties = new FieldCollector(missing)
  parties
    .add('SELLER', partyAddress(findParty(detail, 'SELLER'), { dict }))
    .add('BUYER', partyAddress(findParty(detail, 'BUYER'), { dict }))
    .add('SHIP-TO', partyAddress(findParty(detail, 'CONSIGNEE'), { dict }))
    .add('MANUFACTURER', partyAddress(findParty(detail, 'MANUFACTURER'), { dict }))
    .add('CONTAINER STUFFING LOCATION', partyAddress(findParty(detail, 'PACKING_LOCATION'), { dict }))
    .add('CONSOLIDATOR', partyAddress(findParty(detail, 'PACKING_PARTY'), { dict }))

  const cargoRows = rowsOf(detail, 'cargoList').map(row => [
    text(row.hsCode) || DASH,
    dictCountry(dict, row.countryOfOrigin) || DASH,
  ])

  const sections: CertificateSection[] = [
    gridSection(2, info.fields),
    gridSection(2, parties.fields, 'PARTY INFORMATION'),
    tableSection('CARGOS', ['HARMONIZED #', 'COUNTRY OF ORIGIN'], cargoRows, missing),
    logsSection(context.receipts, missing),
  ]

  return {
    product: 'ISF',
    title: CERTIFICATE_TITLES.ISF,
    issuedAt: formatDateTime(
      receiptValue(context.receipts, 'receiptTime') ||
        pick(detail, 'receiptTime') ||
        pick(detail, 'dateModified')
    ),
    sections,
    missing: dedupeMissing(missing),
  }
}

registerCertificateBuilder('ISF', buildIsfCertificate)
